import React, { useContext } from 'react';
import { Query } from '@apollo/client/react/components';
import query from './Query/BokfaringsarQuery.gql';
import {
  Bokforingsar as IBokforingsarData,
  BokforingsarVariables as IBokforingsarVariables,
} from './Query/_QueryTypes/Bokforingsar';
import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { isPeriodSpanLocked, insertPeriod } from './Utils/BokforingsarUtils';
import { DateInputType, getYearFromDate } from '../../Utils/dateUtils';
import { ActiveDatabaseContext } from '../ActiveDatabaseContext/ActiveDatabaseContext';

interface IProps {
  children: (actions: IBokforingsarActions) => React.ReactNode;
}

interface IInnerProps extends IProps {
  loading: boolean;
  data: IBokforingsarData | undefined;
  error: ApolloError | undefined;
  refetchPeriods: RefetchPeriodsFnType;
}

export type IsPeriodLockedFnType = (
  year?: number,
  fromMonth?: number,
  toMonth?: number,
  dateInputType?: DateInputType
) => boolean;

export type RefetchPeriodsFnType = (
  variables?: IBokforingsarVariables | undefined
) => Promise<ApolloQueryResult<IBokforingsarData>>;

interface IBokforingsarActions {
  isPeriodLocked?: IsPeriodLockedFnType;
  hasExtraPeriod?: (year: number) => boolean;
  refetchPeriods: RefetchPeriodsFnType;
}

class BokforingsarActions extends React.Component<IInnerProps> {
  isPeriodLocked: IsPeriodLockedFnType = (
    year,
    month,
    toMonth,
    dateInputType
  ): boolean => {
    const { data } = this.props;
    if (!year || !month) {
      return true; // TODO: kolla med Lotta vad som ska vara default alternativt inte visas alls. Behöver också kolla upp vad som gäller vid intervall
    }
    if (data && data.db && data.db.SYSBokfar_All && data.db.SYSBokfPeriod_All) {
      if (dateInputType != null && dateInputType === DateInputType.year) {
        const toMonth = this.hasExtraPeriod(year) ? 13 : 12;
        return isPeriodSpanLocked(
          insertPeriod(data.db.SYSBokfar_All, data.db.SYSBokfPeriod_All),
          year,
          1,
          toMonth
        );
      } else {
        return isPeriodSpanLocked(
          insertPeriod(data.db.SYSBokfar_All, data.db.SYSBokfPeriod_All),
          year,
          month,
          toMonth
        );
      }
    } else {
      throw new Error('Could not get SYSBokfar_All');
    }
  };

  hasExtraPeriod = (year: number): boolean => {
    const { data } = this.props;
    if (data && data.db && data.db.SYSBokfar_All && data.db.SYSBokfPeriod_All) {
      const bokforingsAr = data.db.SYSBokfar_All.find(
        x => year === getYearFromDate(x.BOKF_AR)
      );
      const bokfPerioder = data.db.SYSBokfPeriod_All?.filter(
        bokfperiod => year === getYearFromDate(bokfperiod.BOKF_AR)
      );
      if (bokforingsAr && bokfPerioder) {
        const hasExtraPeriod = bokfPerioder.find(x => x.PERIOD === 13);
        return Boolean(hasExtraPeriod);
      }
      return false;
    } else {
      throw new Error('Could not get SYSBokfar_All');
    }
  };

  render() {
    const { error, loading, data } = this.props;

    const hasData = !error && !loading && Boolean(data);

    return this.props.children({
      isPeriodLocked: hasData ? this.isPeriodLocked : undefined,
      hasExtraPeriod: hasData ? this.hasExtraPeriod : undefined,
      refetchPeriods: this.props.refetchPeriods,
    });
  }
}

const EnhancedBokforingsarActions: React.FunctionComponent<IProps> = props => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);

  return (
    <Query<IBokforingsarData, IBokforingsarVariables>
      fetchPolicy="cache-and-network"
      notifyOnNetworkStatusChange
      query={query}
      variables={{ databaseName: activeDatabaseContext.activeDatabase || '' }}
    >
      {({ data, error, loading, refetch }) => {
        return (
          <BokforingsarActions
            refetchPeriods={refetch}
            loading={loading}
            data={data}
            error={error}
            {...props}
          />
        );
      }}
    </Query>
  );
};

export default EnhancedBokforingsarActions;
